import { Close } from '@mui/icons-material';
import {
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    IconButton,
    InputAdornment,
    Stack,
    Switch,
    TextField,
    Typography,
} from '@mui/material';
import Button from '@mui/material/Button';
import { DateTimePicker } from '@mui/x-date-pickers';
import React from 'react';
import { useFormik } from 'formik';
import { useEffect } from 'react';
import * as yup from 'yup';
import { IMaskInput } from 'react-imask';
import useErrors from '../../../hooks/useErrors';
import server from '../../../services/server';

const CommissionField = React.forwardRef(function CommissionField(props, ref) {
    const { onChange, value, ...other } = props;
    return <IMaskInput {...other} value={String(value)} inputRef={ref} onAccept={value => onChange(props.name, value)} />;
});

const isPercentual = is => {
    if (is) {
        return {
            mask: 'num%',
            unmask: true,
            blocks: {
                num: {
                    mask: Number,
                    scale: 2,
                    radix: ',',
                    min: 0,
                    max: 100,
                },
            },
        };
    } else {
        return {
            mask: 'R$ num',
            unmask: true,
            blocks: {
                num: {
                    mask: Number,
                    scale: 2,
                    thousandsSeparator: '.',
                    radix: ',',
                },
            },
        };
    }
};

const offerSchema = yup.object().shape({
    name: yup.string().required('Informe o nome'),
    description: yup.string(),
    value: yup.number().typeError('Informe um valor numérico'),
    avaliableQty: yup.number().typeError('Informe um número'),
});

export default function DialogOffer({ open, onClose, offer, advertiserId, onSuccess }) {
    const { setErrors, enqueueSnackbar } = useErrors();

    const formik = useFormik({
        enableReinitialize: true,
        validationSchema: offerSchema,
        initialValues: {
            name: offer?.name || '',
            label: offer?.label || '',
            description: offer?.description || '',
            privateDescription: offer?.privateDescription || '',
            value: offer?.value || '',
            offerValue: offer?.offerValue ?? '',
            offerValueFrom: offer?.offerValueFrom ?? '',
            avaliableQty: offer?.avaliableQty || '',
            rules: offer?.rules || '',
            isVoucher: true, // Sempre voucher por padrão
            isPercentual: offer?.isPercentual || false,
            expirationAt: offer?.expirationAt || '',
            status: typeof offer?.status === 'boolean' ? offer.status : true,
        },
        onSubmit: async (values, { setSubmitting }) => {
            try {
                setSubmitting(true);
                const payload = {
                    ...values,
                    value: values.value ? Number(values.value) : undefined,
                    offerValue: values.offerValue === '' || values.offerValue == null ? null : Number(values.offerValue),
                    offerValueFrom: values.offerValueFrom === '' || values.offerValueFrom == null ? null : Number(values.offerValueFrom),
                    avaliableQty: values.avaliableQty ? Number(values.avaliableQty) : undefined,
                    isVoucher: true, // Sempre voucher
                    isCoupon: false, // Sempre false para voucher
                    isCashback: false, // Sempre false para voucher
                    expirationAt: values.expirationAt
                        ? values.expirationAt instanceof Date
                            ? values.expirationAt.toISOString()
                            : values.expirationAt
                        : null,
                };

                if (offer?.id) {
                    await server.put(`club/advertisers/${advertiserId}/offers/${offer.id}`, payload);
                    enqueueSnackbar(`Oferta ${values.name} alterada.`);
                } else {
                    await server.post(`club/advertisers/${advertiserId}/offers`, payload);
                    enqueueSnackbar(`Oferta ${values.name} adicionada.`);
                }
                await onSuccess?.();
                onClose();
                setSubmitting(false);
            } catch (error) {
                setSubmitting(false);
                setErrors(error);
            }
        },
    });

    useEffect(() => {
        if (offer) {
            formik.setValues({
                name: offer.name || '',
                label: offer.label || '',
                description: offer.description || '',
                privateDescription: offer.privateDescription || '',
                value: offer.value || '',
                offerValue: offer.offerValue ?? '',
                offerValueFrom: offer.offerValueFrom ?? '',
                avaliableQty: offer.avaliableQty || '',
                rules: offer.rules || '',
                isVoucher: true,
                isPercentual: offer.isPercentual || false,
                expirationAt: offer.expirationAt || '',
                status: typeof offer.status === 'boolean' ? offer.status : true,
            });
        } else {
            formik.resetForm();
        }
    }, [offer, open]);

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <Stack direction="row" justifyContent="space-between" alignItems="center">
                <DialogTitle>{offer ? 'Editando oferta' : 'Adicionando oferta'}</DialogTitle>
                <IconButton size="small" onClick={onClose} sx={{ mr: 2 }}>
                    <Close />
                </IconButton>
            </Stack>
            <Divider />
            <DialogContent dividers component="form" onSubmit={formik.handleSubmit}>
                <Stack rowGap={2} pt={2}>
                    <TextField
                        onChange={formik.handleChange}
                        label="Titulo da oferta"
                        name="name"
                        value={formik.values.name}
                        error={formik.touched.name && Boolean(formik.errors.name)}
                        helperText={formik.touched.name && formik.errors.name}
                    />
                    <TextField
                        onChange={formik.handleChange}
                        label="Label"
                        name="label"
                        value={formik.values.label}
                    />

                    <TextField
                        rows={3}
                        multiline
                        onChange={formik.handleChange}
                        label="Descrição da oferta"
                        name="description"
                        value={formik.values.description}
                    />

                    <TextField
                        rows={2}
                        multiline
                        onChange={formik.handleChange}
                        label="Regras gerais"
                        name="rules"
                        value={formik.values.rules}
                    />

                    <TextField
                        type="number"
                        onChange={formik.handleChange}
                        label="Quantidade disponível"
                        name="avaliableQty"
                        value={formik.values.avaliableQty}
                    />

                    <TextField
                        fullWidth
                        label="Preço do produto"
                        name="offerValue"
                        value={String(formik.values.offerValue ?? '')}
                        onChange={formik.setFieldValue}
                        InputProps={{
                            inputProps: { ...isPercentual(false) },
                            inputComponent: CommissionField,
                        }}
                    />

                    <TextField
                        fullWidth
                        label="Preço de origem"
                        name="offerValueFrom"
                        value={String(formik.values.offerValueFrom ?? '')}
                        onChange={formik.setFieldValue}
                        InputProps={{
                            inputProps: { ...isPercentual(false) },
                            inputComponent: CommissionField,
                        }}
                    />

                    <TextField
                        fullWidth
                        label="Valor Desconto Voucher"
                        name="value"
                        value={String(formik.values.value)}
                        onChange={formik.setFieldValue}
                        error={formik.touched.value && Boolean(formik.errors.value)}
                        helperText={formik.touched.value && formik.errors.value}
                        InputProps={{
                            inputProps: { ...isPercentual(formik.values.isPercentual) },
                            inputComponent: CommissionField,
                            endAdornment: (
                                <InputAdornment position="end">
                                    <FormControlLabel
                                        sx={{ m: 0, p: 0 }}
                                        control={
                                            <Checkbox
                                                checked={formik.values.isPercentual}
                                                onChange={e =>
                                                    formik.setFieldValue('isPercentual', e.target.checked)
                                                }
                                            />
                                        }
                                        label="%"
                                    />
                                </InputAdornment>
                            ),
                        }}
                    />

                    {Boolean(offer?.id) && (
                        <>
                            <DateTimePicker
                                value={formik.values.expirationAt ? new Date(formik.values.expirationAt) : null}
                                format="dd/MM/yyyy HH:mm"
                                onChange={e => formik.setFieldValue('expirationAt', e)}
                                name="expirationAt"
                                label="Expira em"
                                slotProps={{
                                    textField: {
                                        error: formik.touched.expirationAt && Boolean(formik.errors.expirationAt),
                                        helperText: formik.touched.expirationAt && formik.errors.expirationAt,
                                        fullWidth: true,
                                    },
                                    field: {
                                        clearable: true,
                                        onClear: () => formik.setFieldValue('expirationAt', ''),
                                    },
                                }}
                            />
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formik.values.status}
                                        onChange={e => formik.setFieldValue('status', e.target.checked)}
                                    />
                                }
                                label="Status da oferta"
                            />
                        </>
                    )}
                    <Divider> Dados internos </Divider>
                    <TextField
                        rows={3}
                        multiline
                        onChange={formik.handleChange}
                        label="Observações (Privado)"
                        name="privateDescription"
                        value={formik.values.privateDescription}
                    />
                </Stack>
            </DialogContent>
            <Divider />
            <DialogActions>
                <Stack direction="row" alignItems="center" spacing={2}>
                    <Button variant="outlined" onClick={onClose}>
                        Cancelar
                    </Button>
                    <Button variant="contained" onClick={formik.handleSubmit} disabled={formik.isSubmitting}>
                        {formik.isSubmitting ? 'Salvando...' : 'Salvar e continuar'}
                    </Button>
                </Stack>
            </DialogActions>
        </Dialog>
    );
}
